/**
 * Read-only mirror of the delivery catalog in supabase/functions/_shared/sendgridIpPools.ts.
 *
 * WHY A MIRROR AND NOT AN IMPORT
 * The sender runs on Deno and this bundle runs in the browser; they share no module graph. This
 * file is for DISPLAY ONLY — it never decides routing. The server resolves the pool from the
 * stored key against its own catalog and fails closed on an unknown one, so a drift here shows a
 * wrong label, never a wrong send. Keep it in step when the sender's catalog changes.
 *
 * WHAT IT FIXES
 * FlightDetail previously hardcoded a single `=== "usls_email_pool_01"` branch, so a flight on
 * pool 02 rendered as "Current delivery pool (default)" — the opposite of the truth. All 36
 * Partner Newsletter September 2026 flights are on pool 02, so every one of them was mislabelled.
 */

export type SendgridIpPoolKey =
  | "default"
  | "usls_email_pool_01"
  | "usls_email_pool_02";

interface PoolDisplay {
  label: string;
  /** The literal SendGrid pool name, or null when the default pool is used. */
  poolName: string | null;
  /** Categories the pool itself always attaches. */
  categories: string[];
  /** Operator-facing detail: the sending IP and its reverse DNS. */
  detail: string | null;
  isWarmup: boolean;
}

export const SENDGRID_POOL_DISPLAY: Record<SendgridIpPoolKey, PoolDisplay> = {
  default: {
    label: "Current delivery pool (default)",
    poolName: null,
    categories: [],
    detail: null,
    isWarmup: false,
  },
  usls_email_pool_01: {
    label: "usls-email-pool-01 — warmup",
    poolName: "usls-email-pool-01",
    categories: ["usls-email-pool-01-warmup"],
    detail: "159.183.13.156 · o1.newsletter.uslawshield.com",
    isWarmup: true,
  },
  usls_email_pool_02: {
    label: "usls-email-pool-02 — warmup",
    poolName: "usls-email-pool-02",
    categories: ["usls-email-pool-02-warmup"],
    detail: "159.183.87.246 · o2.newsletter01.2ainstitute.com",
    isWarmup: true,
  },
};

export const resolvePoolDisplay = (
  key: string | null | undefined,
): PoolDisplay =>
  SENDGRID_POOL_DISPLAY[(key ?? "default") as SendgridIpPoolKey] ??
  SENDGRID_POOL_DISPLAY.default;

/**
 * The categories a flight will actually send with: the pool's own, plus the campaign's optional
 * reporting category. Mirrors buildSendCategories() on the server, including its dedupe, so the
 * page shows what SendGrid will receive rather than a hopeful approximation.
 */
export const effectiveSendCategories = (
  poolKey: string | null | undefined,
  campaignCategory: string | null | undefined,
): string[] => {
  const out = [...resolvePoolDisplay(poolKey).categories];
  const c = (campaignCategory ?? "").trim();
  if (c && !out.includes(c)) {
    out.push(c);
  }
  return out;
};
